import { CheckCircle2, CircleSlash, Flag, type LucideIcon } from "lucide-react";
import type { RunReport } from "./types";

type Assessment = RunReport["checks"][number]["assessment"];

/**
 * Every finished source row reports one of three outcomes, whatever the
 * source is or what it returned. The row's summary and details explain the
 * specifics (expired, not found, unsupported), so the status only says what
 * the reviewer should do next.
 */
export const CheckOutcome = {
  Valid: "valid",
  ReviewFindings: "review-findings",
  DataNotAvailable: "data-not-available",
} as const;
export type CheckOutcome = (typeof CheckOutcome)[keyof typeof CheckOutcome];

export const CHECK_OUTCOME_PRESENTATION: Record<
  CheckOutcome,
  { label: string; style: string; icon: LucideIcon }
> = {
  [CheckOutcome.Valid]: {
    label: "Valid",
    style: "border-green-200 bg-green-50 text-green-800",
    icon: CheckCircle2,
  },
  [CheckOutcome.ReviewFindings]: {
    label: "Review findings",
    style: "border-amber-200 bg-amber-50 text-amber-900",
    icon: Flag,
  },
  [CheckOutcome.DataNotAvailable]: {
    label: "Data not available",
    style: "border-yellow-200 bg-yellow-50 text-yellow-800",
    icon: CircleSlash,
  },
};

/** A completed review that leaves nothing to act on. */
export const VALID_REVIEWED_LABEL = "Valid - Reviewed";

const OUTCOME_BY_STATUS: Record<
  Exclude<Assessment["status"], "in-progress">,
  CheckOutcome
> = {
  clear: CheckOutcome.Valid,
  "attention-needed": CheckOutcome.ReviewFindings,
  limited: CheckOutcome.ReviewFindings,
  incomplete: CheckOutcome.DataNotAvailable,
};

/** The outcome of an automated assessment, or null while it is still running. */
export function assessmentOutcome(
  assessment: Pick<Assessment, "status"> & { items?: unknown[] },
): CheckOutcome | null {
  if (assessment.status === "in-progress") {
    return null;
  }
  // A collected check with no saved result searched nothing reviewable.
  if (assessment.status === "limited" && assessment.items?.length === 0) {
    return CheckOutcome.DataNotAvailable;
  }
  return OUTCOME_BY_STATUS[assessment.status];
}
